import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/useReduxHooks";
import { selectAuth, setAuth } from "features/authSlice";
import { SSEManager } from "../components/SSEManager";
import { AppRoutes } from "../types/enums";
import { useLogout, useNotFoundRedirect, useSetPermissions } from "../hooks";
import { useGetUserQuery, useLoginWithLinkMutation } from "../api";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CustomToast } from "../components/ui/CustomToast";
import { CustomLoader } from "../components/ui/CustomLoader";
import { selectPermissions } from "../features/permissionsSlice";

export const PrivateLayout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAllowedEditCreateNewCampaign } = useAppSelector(selectPermissions);
  const { accessToken } = useAppSelector(selectAuth);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const logout = useLogout();
  const [loginWithLink] = useLoginWithLinkMutation();
  const {
    isLoading: isUserLoading,
    currentData,
    error,
  } = useGetUserQuery(undefined, {
    skip: !accessToken,
  });

  useSetPermissions(currentData?.permissions);
  useNotFoundRedirect({ error, withLogout: true });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("login_link_token");
    const email = urlParams.get("email");
    const redirect_url = urlParams.get("redirect_url");

    if (token && email) {
      setIsLoading(true);
      loginWithLink({ email, token })
        .unwrap()
        .then((data) => {
          localStorage.setItem("success", "is login");
          dispatch(
            setAuth({
              accessToken: data.access_token,
              refreshToken: data.refresh_token,
            }),
          );
          if (redirect_url) {
            navigate(redirect_url, { replace: true });
          } else {
            toast.success(
              <CustomToast
                type="success"
                text="You’ve successfully logged in using a temporary access link."
                title="Welcome back!"
              />,
            );
            navigate(AppRoutes.CAMPAIGNS, { replace: true });
          }
        })
        .catch(() => {
          toast.error(
            <CustomToast
              type="error"
              text="The temporary login link is no longer valid. Request a new one to access the platform."
              title="This link has expired"
            />,
          );
          logout();
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!(isLoading || isUserLoading) && !currentData && !error) {
      logout();
      navigate(AppRoutes.LOGIN, { replace: true });
    }
  }, [currentData, isLoading, isUserLoading, error]);

  if (isLoading || isUserLoading || !currentData) return <CustomLoader />;

  if (currentData.is_first_login) {
    return <Navigate to={AppRoutes.Welcome} />;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const redirect_url = urlParams.get("redirect_url");

  if (currentData.is_client && !redirect_url) {
    logout();
    return <Navigate to={AppRoutes.LOGIN} replace />;
  }

  if (isAllowedEditCreateNewCampaign === null) return <CustomLoader />;

  return (
    <>
      <SSEManager />
      <Outlet />
    </>
  );
};
